import React, { createContext, useContext, useState, useEffect } from 'react';
import { BlogPost } from '../types';

interface BlogContextType {
  posts: BlogPost[];
  addPost: (post: Omit<BlogPost, 'id'>) => Promise<string>;
  updatePost: (id: string, post: Partial<BlogPost>) => Promise<void>;
  deletePost: (id: string) => Promise<void>;
  isLoading: boolean;
}

const BlogContext = createContext<BlogContextType | undefined>(undefined);

export const useBlogContext = () => {
  const context = useContext(BlogContext);
  if (!context) {
    throw new Error('useBlogContext must be used within a BlogProvider');
  }
  return context;
};

export const BlogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const { supabase } = await import('../services/supabase');
        const { data, error } = await supabase
          .from('news')
          .select('*')
          .order('date', { ascending: false });
        
        if (data) {
          setPosts(data as BlogPost[]);
        }
      } catch (e) {
        console.error("Failed to load blog posts", e);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPosts();

    // Subscribe to real-time updates for news using dynamic import
    let channel: any;
    const setupSubscription = async () => {
      try {
        const { supabase } = await import('../services/supabase');
        channel = supabase
          .channel('news_changes')
          .on('postgres_changes', { event: '*', schema: 'public', table: 'news' }, () => {
            // Re-run fetch logic
            const refetch = async () => {
               const { data } = await supabase.from('news').select('*').order('date', { ascending: false });
               if (data) setPosts(data as BlogPost[]);
            };
            refetch();
          })
          .subscribe();
      } catch (e) {
        console.error("Subscription failed", e);
      }
    };

    setupSubscription();

    return () => {
      if (channel) {
        import('../services/supabase').then(({ supabase }) => {
          supabase.removeChannel(channel);
        });
      }
    };
  }, []);

  const addPost = async (postData: Omit<BlogPost, 'id'>) => {
    const { supabase } = await import('../services/supabase');
    const { data, error } = await supabase
      .from('news')
      .insert([postData])
      .select();
    
    if (error) throw error;
    return data[0].id;
  };

  const updatePost = async (id: string, postData: Partial<BlogPost>) => {
    const { supabase } = await import('../services/supabase');
    const { error } = await supabase
      .from('news')
      .update(postData)
      .eq('id', id);
    
    if (error) throw error;
  };

  const deletePost = async (id: string) => {
    const { supabase } = await import('../services/supabase');
    const { error } = await supabase
      .from('news')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  };

  return (
    <BlogContext.Provider value={{ posts, addPost, updatePost, deletePost, isLoading }}>
      {children}
    </BlogContext.Provider>
  );
};